import React, { useCallback, useEffect, useState } from 'react';
import { availabilityAPI } from '../services/api';
import ConfirmDialog from '../components/ConfirmDialog';
import { useAuth } from '../context/AuthContext';
import { useLocalization } from '../context/LocalizationContext';
import { formatDateTime } from '../utils/dateFormat';
import { formatCoordinates, getAppDeviceId, getCurrentCoordinates } from '../utils/geolocation';
import styles from '../styles/ClockPage.module.css';

const ClockPage = () => {
  const { can } = useAuth();
  const { t } = useLocalization();
  const [today, setToday] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [confirmState, setConfirmState] = useState({
    open: false,
    action: null,
    title: '',
    message: '',
    confirmText: 'Confirm',
  });

  const fetchToday = useCallback(async () => {
    try {
      setLoading(true);
      const response = await availabilityAPI.getToday();
      setToday(response.data);
    } catch (error) {
      alert(error.response?.data?.message || t('clock_today_failed', 'Failed to fetch today status'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  const fetchLogs = useCallback(async () => {
    try {
      const response = await availabilityAPI.logs();
      setLogs(response.data || []);
    } catch (error) {
      alert(error.response?.data?.message || t('clock_logs_failed', 'Failed to fetch clock logs'));
    }
  }, [t]);

  useEffect(() => {
    fetchToday();
    fetchLogs();
  }, [fetchToday, fetchLogs]);

  const getClockPayload = async () => {
    const deviceId = await getAppDeviceId();
    try {
      const coords = await Promise.race([
        getCurrentCoordinates({ timeoutMs: 6000, maximumAge: 30000, enableHighAccuracy: false }),
        new Promise((resolve) => setTimeout(() => resolve(null), 2000)),
      ]);

      if (!coords) return deviceId ? { deviceId } : {};

      return {
        latitude: coords.latitude,
        longitude: coords.longitude,
        ...(deviceId ? { deviceId } : {}),
      };
    } catch (error) {
      return deviceId ? { deviceId } : {};
    }
  };

  const handleClockIn = async () => {
    try {
      setSubmitting(true);
      const payload = await getClockPayload();
      await availabilityAPI.clockIn(payload);
      await fetchToday();
      await fetchLogs();
    } catch (error) {
      alert(error.response?.data?.message || t('clock_in_failed', 'Failed to clock in'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClockOut = async () => {
    try {
      setSubmitting(true);
      const payload = await getClockPayload();
      await availabilityAPI.clockOut(payload);
      await fetchToday();
      await fetchLogs();
    } catch (error) {
      alert(error.response?.data?.message || t('clock_out_failed', 'Failed to clock out'));
    } finally {
      setSubmitting(false);
    }
  };

  const isClockedIn = !!today && Number(today.available) === 1 && !today.logout_time;

  const closeConfirmDialog = () => {
    if (submitting) return;
    setConfirmState((prev) => ({ ...prev, open: false }));
  };

  const requestClockIn = () => {
    setConfirmState({
      open: true,
      action: 'clock-in',
      title: t('nav_confirm_clock_in', 'Confirm Clock In'),
      message: t('nav_confirm_clock_in_message', 'Do you want to clock in now?'),
      confirmText: t('nav_clock_in', 'Clock In'),
    });
  };

  const requestClockOut = () => {
    setConfirmState({
      open: true,
      action: 'clock-out',
      title: t('nav_confirm_clock_out', 'Confirm Clock Out'),
      message: t('nav_confirm_clock_out_message', 'Do you want to clock out now?'),
      confirmText: t('nav_clock_out', 'Clock Out'),
    });
  };

  const actionLabelMap = {
    clock_in: t('nav_clock_in', 'Clock In'),
    clock_out: t('nav_clock_out', 'Clock Out'),
  };

  const canDeleteClockEvents = can('availability', 'delete');

  const handleDeleteClockEvent = async (log) => {
    if (!log?.id) return;
    const confirmed = window.confirm(t('clock_delete_day_events_confirm', 'Delete attendance events for this day? This will remove both clock-in and clock-out records for that day.'));
    if (!confirmed) return;

    try {
      setSubmitting(true);
      await availabilityAPI.deleteClockEvent(log.id);
      await fetchToday();
      await fetchLogs();
    } catch (error) {
      alert(error.response?.data?.message || t('clock_delete_day_events_failed', 'Failed to delete attendance events for the selected day'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmDialog = async () => {
    if (confirmState.action === 'clock-in') {
      await handleClockIn();
    }
    if (confirmState.action === 'clock-out') {
      await handleClockOut();
    }
    setConfirmState((prev) => ({ ...prev, open: false }));
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <h1>{t('clock_page_title', 'Daily Attendance')}</h1>
        {loading ? (
          <p>{t('clock_loading', 'Loading...')}</p>
        ) : (
          <>
            <p className={styles.statusLabel}>{t('clock_status', 'Status')}</p>
            <p className={`${styles.status} ${isClockedIn ? styles.active : styles.inactive}`}>
              {isClockedIn ? t('clock_status_in', 'Clocked In') : t('clock_status_out', 'Clocked Out')}
            </p>

            <div className={styles.timeGrid}>
              <div className={styles.timeCard}>
                <span>{t('nav_clock_in', 'Clock In')}</span>
                <strong>{today?.login_time ? formatDateTime(today.login_time) : '-'}</strong>
              </div>
              <div className={styles.timeCard}>
                <span>{t('nav_clock_out', 'Clock Out')}</span>
                <strong>{today?.logout_time ? formatDateTime(today.logout_time) : '-'}</strong>
              </div>
            </div>

            <div className={styles.buttons}>
              <button className={styles.inBtn} onClick={requestClockIn} disabled={submitting || isClockedIn}>
                {t('nav_clock_in', 'Clock In')}
              </button>
              <button className={styles.outBtn} onClick={requestClockOut} disabled={submitting || !isClockedIn}>
                {t('nav_clock_out', 'Clock Out')}
              </button>
            </div>
          </>
        )}
      </div>

      <div className={styles.card}>
        <h2>{t('clock_logs_title', 'Clock Logs')}</h2>
        {logs.length === 0 ? (
          <p>{t('clock_logs_empty', 'No logs yet.')}</p>
        ) : (
          <>
            <div className={styles.logsTableWrap}>
              <table className={styles.logsTable}>
                <thead>
                  <tr>
                    <th>{t('activities_user', 'User')}</th>
                    <th>{t('activities_action', 'Action')}</th>
                    <th>{t('activities_time', 'Time')}</th>
                    <th>{t('common_location', 'Location')}</th>
                    <th>{t('common_gps', 'GPS')}</th>
                    <th>{t('clock_details', 'Details')}</th>
                    {canDeleteClockEvents && <th>{t('common_actions', 'Actions')}</th>}
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id}>
                      <td>{log.username}</td>
                      <td>{actionLabelMap[log.action] || log.action}</td>
                      <td>{formatDateTime(log.action_time)}</td>
                      <td>{log.location_name || '-'}</td>
                      <td>{formatCoordinates(log.latitude, log.longitude) || '-'}</td>
                      <td>{log.details || '-'}</td>
                      {canDeleteClockEvents && (
                        <td>
                          {(log.action === 'clock_in' || log.action === 'clock_out') ? (
                            <button
                              type="button"
                              className={styles.deleteClockInBtn}
                              onClick={() => handleDeleteClockEvent(log)}
                              disabled={submitting}
                            >
                              {t('common_delete', 'Delete')}
                            </button>
                          ) : '-'}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={styles.logsMobileList}>
              {logs.map((log) => (
                <div key={`mobile-${log.id}`} className={styles.logMobileCard}>
                  <div className={styles.logMobileRow}><strong>{t('activities_user', 'User')}:</strong> {log.username}</div>
                  <div className={styles.logMobileRow}><strong>{t('activities_action', 'Action')}:</strong> {actionLabelMap[log.action] || log.action}</div>
                  <div className={styles.logMobileRow}><strong>{t('activities_time', 'Time')}:</strong> {formatDateTime(log.action_time)}</div>
                  <div className={styles.logMobileRow}><strong>{t('common_location', 'Location')}:</strong> {log.location_name || '-'}</div>
                  <div className={styles.logMobileRow}><strong>{t('common_gps', 'GPS')}:</strong> {formatCoordinates(log.latitude, log.longitude) || '-'}</div>
                  <div className={styles.logMobileRow}><strong>{t('clock_details', 'Details')}:</strong> {log.details || '-'}</div>
                  {canDeleteClockEvents && (log.action === 'clock_in' || log.action === 'clock_out') && (
                    <div className={styles.logMobileActions}>
                      <button
                        type="button"
                        className={styles.deleteClockInBtn}
                        onClick={() => handleDeleteClockEvent(log)}
                        disabled={submitting}
                      >
                        {t('common_delete', 'Delete')}
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmState.open}
        title={confirmState.title}
        message={confirmState.message}
        confirmText={confirmState.confirmText}
        onConfirm={handleConfirmDialog}
        onCancel={closeConfirmDialog}
        loading={submitting}
      />
    </div>
  );
};

export default ClockPage;
